import { useGetCentralBranches } from '@agos/api-client/platform'
import { normaliseHost } from '@agos/customer-core'
import { useMemo } from 'react'

import { registryOf, type Branch } from './branches'

/**
 * The brand's branches, and which one this is.
 *
 * The gate, the header chip and the footer line all need the same answer, and it is **server-owned** —
 * so it lives in the query cache rather than in the zustand store beside the preference. Legacy keeps
 * the list in its store and pays for it: the store is populated by whichever component mounted first,
 * and the header renders "Pobočka" until that happens.
 *
 * `staleTime: Infinity` — a brand does not gain a branch while somebody is ordering, and `BranchGate`
 * already asks with the same key, so this is the same request, not a second one.
 */
export function useBranches(): { branches: Branch[]; current: Branch | null } {
  const { data } = useGetCentralBranches(undefined, {
    query: { staleTime: Number.POSITIVE_INFINITY, retry: 1 },
  })
  return useMemo(() => {
    const registry = registryOf(data)
    const host = normaliseHost(typeof location === 'undefined' ? '' : location.hostname)
    const current =
      registry.branches.find((branch) => normaliseHost(branch.primaryDomain) === host) ??
      registry.branches.find((branch) => branch.id === registry.currentId) ??
      null
    return { branches: registry.branches, current }
  }, [data])
}
